import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, Link } from "react-router-dom";
import {
  ForgotPasswordRequestSchema,
  type ForgotPasswordRequest,
} from "../../domain/types/password-reset";
import { passwordResetApiService } from "../../infrastructure/api/password-reset-api-service";
import { Input } from "../components/UI/Input";
import { Button } from "../components/UI/Button";

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(ForgotPasswordRequestSchema),
  });

  const onSubmit = async (data: ForgotPasswordRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await passwordResetApiService.requestPasswordReset(data);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || "요청 처리 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#E8EFFF] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-[#4285F4]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#222] mb-3">
              이메일을 확인해 주세요
            </h2>
            <p className="text-[#555] mb-6">
              입력하신 이메일로 비밀번호 재설정 링크를 발송했습니다.
              <br />
              이메일을 확인하고 링크를 클릭해 주세요.
            </p>
            <div className="bg-[#F5F5F5] rounded-[8px] p-4 mb-6">
              <p className="text-sm text-[#888]">
                • 이메일이 도착하지 않았다면 스팸함을 확인해 주세요.
                <br />• 링크는 30분간 유효합니다.
              </p>
            </div>
            <Link to="/">
              <Button
                type="button"
                className="w-full bg-[#222] text-white hover:bg-[#333]"
                style={{ height: "48px", borderRadius: "8px" }}
              >
                메인으로 돌아가기
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-[#222] mb-2">
            비밀번호 찾기
          </h1>
          <p className="text-[#555]">
            가입 시 등록한 이메일과 이름을 입력해 주세요.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="bg-[#EBF3FF] border border-[#4285F4]/20 rounded-[8px] p-3 mb-4">
              <p className="text-sm text-[#4285F4]">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#555] mb-1">
                이메일
              </label>
              <Input
                type="email"
                placeholder="이메일을 입력해 주세요"
                {...register("email")}
                error={errors.email?.message}
                style={{
                  width: "100%",
                  height: "48px",
                  borderRadius: "8px",
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#555] mb-1">
                이름
              </label>
              <Input
                type="text"
                placeholder="이름을 입력해 주세요"
                {...register("name")}
                error={errors.name?.message}
                style={{
                  width: "100%",
                  height: "48px",
                  borderRadius: "8px",
                }}
              />
            </div>
          </div>

          <div className="mt-6">
            <Button
              type="submit"
              loading={isLoading}
              className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white font-semibold"
              style={{ height: "48px", borderRadius: "8px" }}
            >
              비밀번호 재설정 이메일 보내기
            </Button>
          </div>

          <div className="mt-4 text-center">
            <Link
              to="/"
              className="text-sm text-[#4285F4] hover:text-[#3367D6] hover:underline"
            >
              로그인으로 돌아가기
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
